import {
  type DocumentMeta,
  type DocumentVersion
} from "@prismicio/editor-fields"

import type { DocumentVersionStatus } from "../../../app/services/core"

/**
 * Creates map of documentIds to documentIds in other locale
 */
export function createTranslationMap(args: {
  originalDocs: string[]
  groupLangIds: { id: string; groupLangId: string }[]
  translationIds: { id: string; groupLangId: string }[]
}): Map<string, string> {
  const { originalDocs, groupLangIds, translationIds } = args

  const groupLangsMap = new Map(groupLangIds.map((lng) => [lng.id, lng]))
  const translatedDocsMap = new Map(
    translationIds.map((doc) => [doc.groupLangId, doc])
  )

  const translatedDocs = new Map<string, string>()
  for (const documentId of originalDocs) {
    const documentLangId = groupLangsMap.get(documentId)
    if (!documentLangId) {
      continue
    }

    const translatedDocId = translatedDocsMap.get(documentLangId.groupLangId)
    if (!translatedDocId) {
      continue
    }

    translatedDocs.set(documentId, translatedDocId.id)
  }

  return translatedDocs
}

/**
 * Find the preferred document version from a list of documents (same page in different locales)
 */
export function findExistingLocaleVersion(
  documents: DocumentMeta[],
  currentStatus: DocumentVersionStatus
): DocAndVersion | undefined {
  const versions = documents.flatMap((doc) =>
    doc.versions.map((version) => ({ doc, version }))
  )

  // First, try to find an exact match for the current version's status
  const exactVersion = versions.find(
    ({ version }) => version.status === currentStatus
  )

  if (exactVersion) return exactVersion

  const statusPreferenceOrder = [
    "unclassified",
    "published",
    "release",
    "archived"
  ]

  for (const nextBestStatus of statusPreferenceOrder) {
    const version = versions.find(
      ({ version }) => version.status === nextBestStatus
    )

    if (version) return version
  }

  // Couldn't find any interesting versions
  return
}

interface DocAndVersion {
  doc: DocumentMeta
  version: DocumentVersion
}
